/**
 * Month-End (Scheduled) Reports – App2 version.
 * Uses improve-api /api/_internal/scheduled-reports and app2 data layer.
 * Layout and styles match the legacy ManagerView ScheduledReports page.
 */
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import { AdminHeader } from "@/pages/ManagerView/AdminHeader";
import {
  useGetScheduledReportsSaved,
  useGetScheduledReportById,
  useGenerateScheduledReport,
  useRegenerateScheduledReport,
  downloadScheduledReportPdf,
} from "@/app2/shared/data";
import { useImproveAxios } from "@/app2/shared/hooks/useImproveAxios";
import { PAGE_SIZE, sortReportData, type SortOrder } from "./utils";
import { ScheduledReportsOverviewCards } from "./ScheduledReportsOverviewCards";
import { ScheduledReportsMonthSelector } from "./ScheduledReportsMonthSelector";
import { ScheduledReportsTopPerformers } from "./ScheduledReportsTopPerformers";
import type { ColumnConfig } from "./ScheduledReportsInsightsTable";
import { ScheduledReportsInsightsTable } from "./ScheduledReportsInsightsTable";
import { ScheduledReportsUsersWithoutTable } from "./ScheduledReportsUsersWithoutTable";
import { RegenerateReportModal } from "./RegenerateReportModal";
import PeopleIcon from "@mui/icons-material/People";
import { useNavigate } from "react-router-dom";
import { useDevCycleSdk } from "@/app2/shared/hooks";

const DIVISION_COLUMNS: ColumnConfig[] = [
  { label: "Division", key: "Division", format: "text" },
  { label: "Team Size", key: "Team Size", format: "text" },
  { label: "Total Watch Time", key: "Total Watch Time (hours)", format: "hours" },
  { label: "Avg. Watch Time", key: "Avg. Watch Time (hours)", format: "hours" },
  { label: "Total Lesson Completed", key: "Total Lesson Completed", format: "text" },
  { label: "Avg. Completed Lessons", key: "Avg. Completed Lessons", format: "text" },
  { label: "Overdue Lessons", key: "Overdue Lessons", format: "text" },
];

const SUPERVISOR_COLUMNS: ColumnConfig[] = [
  { label: "Supervisor", key: "Supervisor", format: "text" },
  { label: "Team Size", key: "Team Size", format: "text" },
  { label: "Total Watch Time", key: "Total Watch Time (hours)", format: "hours" },
  { label: "Avg. Watch Time", key: "Avg. Watch Time (hours)", format: "hours" },
  { label: "Total Completed Lessons", key: "Total Completed Lessons", format: "text" },
  { label: "Avg. Completed Lessons", key: "Avg. Completed Lessons", format: "text" },
  { label: "Overdue Lessons", key: "Overdue Lessons", format: "text" },
];

export const AdminScheduledReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const { monthlyReportDistributionList } = useDevCycleSdk();

  const [selectedMonth, setSelectedMonth] = useState(() => dayjs().subtract(1, "month"));
  const [monthPickerAnchor, setMonthPickerAnchor] = useState<HTMLElement | null>(null);
  const [pickerYear, setPickerYear] = useState(() => dayjs().subtract(1, "month").year());
  const [divisionsPage, setDivisionsPage] = useState(1);
  const [supervisorsPage, setSupervisorsPage] = useState(1);
  const [usersWithoutPage, setUsersWithoutPage] = useState(1);
  const [divisionsSort, setDivisionsSort] = useState<{ column: string | null; order: SortOrder }>({
    column: "Avg. Completed Lessons",
    order: "desc",
  });
  const [supervisorsSort, setSupervisorsSort] = useState<{ column: string | null; order: SortOrder }>({
    column: "Avg. Completed Lessons",
    order: "desc",
  });
  const [usersWithoutSort, setUsersWithoutSort] = useState<{ column: string | null; order: SortOrder }>({ column: null, order: "asc" });
  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const axiosConfig = useImproveAxios();
  const { data: savedList, isLoading: savedLoading } = useGetScheduledReportsSaved();
  const availableMonths = useMemo(() => {
    if (!savedList) return [];
    return savedList.map((r) => ({
      month: r.monthId - 1,
      year: r.yearId,
      label: r.monthName,
    }));
  }, [savedList]);

  const hasReport = useCallback(
    (monthIndex: number, year: number) =>
      availableMonths.some((m) => m.month === monthIndex && m.year === year),
    [availableMonths]
  );

  const availableYears = useMemo(() => {
    const years = new Set(availableMonths.map((m) => m.year));
    return Array.from(years).sort((a, b) => b - a);
  }, [availableMonths]);

  const handleMonthPickerOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setPickerYear(selectedMonth.year());
    setMonthPickerAnchor(event.currentTarget);
  }, [selectedMonth]);

  const handleMonthPickerClose = useCallback(() => setMonthPickerAnchor(null), []);

  const handleMonthSelect = useCallback(
    (monthIndex: number) => {
      if (hasReport(monthIndex, pickerYear)) {
        setSelectedMonth(dayjs().year(pickerYear).month(monthIndex));
        handleMonthPickerClose();
      }
    },
    [pickerYear, hasReport, handleMonthPickerClose]
  );

  const handlePickerYearChange = useCallback(
    (delta: number) => {
      const newYear = pickerYear + delta;
      if (availableYears.includes(newYear)) setPickerYear(newYear);
    },
    [pickerYear, availableYears]
  );

  const canGoToPreviousYear = availableYears.some((y) => y < pickerYear);
  const canGoToNextYear = availableYears.some((y) => y > pickerYear);

  const selectedReportId = useMemo(() => {
    if (!savedList) return null;
    const r = savedList.find(
      (x) => x.monthId === selectedMonth.month() + 1 && x.yearId === selectedMonth.year()
    );
    return r?.id ?? null;
  }, [savedList, selectedMonth]);

  const { data: reportDataResp, isLoading: reportLoading } = useGetScheduledReportById(selectedReportId);
  const generateMutation = useGenerateScheduledReport();
  const regenerateMutation = useRegenerateScheduledReport();

  const reportData = reportDataResp?.reportData;
  const overviewData = reportData?.overview;
  const topPerformersData = reportData?.topPerformers ?? [];
  const divisionsData = reportData?.divisions ?? [];
  const supervisorsData = reportData?.supervisors ?? [];
  const usersWithoutData = reportData?.usersWithout ?? [];

  const hasNoReports = !savedLoading && (!savedList || savedList.length === 0);
  const isLoading = savedLoading || reportLoading;

  const lastMonth = dayjs().subtract(1, "month");
  const canGenerateLastMonth = !savedList?.some(
    (r) => r.monthId === lastMonth.month() + 1 && r.yearId === lastMonth.year()
  );

  useEffect(() => {
    if (
      availableMonths.length > 0 &&
      !savedList?.some(
        (r) => r.monthId === selectedMonth.month() + 1 && r.yearId === selectedMonth.year()
      )
    ) {
      const sorted = [...availableMonths].sort((a, b) => {
        const dateA = dayjs().year(a.year).month(a.month);
        const dateB = dayjs().year(b.year).month(b.month);
        return dateB.diff(dateA);
      });
      if (sorted[0]) setSelectedMonth(dayjs().year(sorted[0].year).month(sorted[0].month));
    }
  }, [availableMonths.length]);

  const goToPreviousMonth = useCallback(() => {
    const older = availableMonths
      .filter((m) => dayjs().year(m.year).month(m.month).isBefore(selectedMonth, "month"))
      .sort((a, b) => dayjs().year(b.year).month(b.month).diff(dayjs().year(a.year).month(a.month)));
    if (older[0]) setSelectedMonth(dayjs().year(older[0].year).month(older[0].month));
  }, [selectedMonth, availableMonths]);

  const goToNextMonth = useCallback(() => {
    const newer = availableMonths
      .filter((m) => dayjs().year(m.year).month(m.month).isAfter(selectedMonth, "month"))
      .sort((a, b) => dayjs().year(a.year).month(a.month).diff(dayjs().year(b.year).month(b.month)));
    if (newer[0]) setSelectedMonth(dayjs().year(newer[0].year).month(newer[0].month));
  }, [selectedMonth, availableMonths]);

  const canGoPrevious = availableMonths.some((m) =>
    dayjs().year(m.year).month(m.month).isBefore(selectedMonth, "month")
  );
  const canGoNext = availableMonths.some((m) =>
    dayjs().year(m.year).month(m.month).isAfter(selectedMonth, "month")
  );

  const handleGenerate = useCallback(() => {
    generateMutation.mutate({ month: lastMonth.month() + 1, year: lastMonth.year() });
  }, [lastMonth, generateMutation]);

  const handleRegenerate = useCallback(() => {
    setRegenerateModalOpen(false);
    regenerateMutation.mutate({
      month: selectedMonth.month() + 1,
      year: selectedMonth.year(),
    });
  }, [selectedMonth, regenerateMutation]);

  const handleDownload = useCallback(async () => {
    if (!selectedReportId || !reportData) return;
    setDownloadError(null);
    setIsDownloading(true);
    try {
      const accountName = reportData.accountName ?? "Report";
      const monthName = reportData.monthName ?? "Report";
      const filename = `${accountName}-${monthName}.pdf`.replace(/\s+/g, "-");
      await downloadScheduledReportPdf(selectedReportId, filename, axiosConfig);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : "Download failed");
    } finally {
      setIsDownloading(false);
    }
  }, [selectedReportId, reportData, axiosConfig]);

  const sortedDivisions = useMemo(
    () => sortReportData(divisionsData, divisionsSort.column, divisionsSort.order),
    [divisionsData, divisionsSort]
  );
  const sortedSupervisors = useMemo(
    () => sortReportData(supervisorsData, supervisorsSort.column, supervisorsSort.order),
    [supervisorsData, supervisorsSort]
  );
  const sortedUsersWithout = useMemo(
    () => sortReportData(usersWithoutData, usersWithoutSort.column, usersWithoutSort.order),
    [usersWithoutData, usersWithoutSort]
  );

  const paginatedDivisions = useMemo(
    () => sortedDivisions.slice((divisionsPage - 1) * PAGE_SIZE, divisionsPage * PAGE_SIZE),
    [sortedDivisions, divisionsPage]
  );
  const paginatedSupervisors = useMemo(
    () => sortedSupervisors.slice((supervisorsPage - 1) * PAGE_SIZE, supervisorsPage * PAGE_SIZE),
    [sortedSupervisors, supervisorsPage]
  );
  const paginatedUsersWithout = useMemo(
    () => sortedUsersWithout.slice((usersWithoutPage - 1) * PAGE_SIZE, usersWithoutPage * PAGE_SIZE),
    [sortedUsersWithout, usersWithoutPage]
  );

  const handleDivisionsSort = useCallback((column: string) => {
    setDivisionsSort((prev) => ({
      column,
      order: prev.column === column && prev.order === "asc" ? "desc" : "asc",
    }));
    setDivisionsPage(1);
  }, []);

  const handleSupervisorsSort = useCallback((column: string) => {
    setSupervisorsSort((prev) => ({
      column,
      order: prev.column === column && prev.order === "asc" ? "desc" : "asc",
    }));
    setSupervisorsPage(1);
  }, []);

  const handleUsersWithoutSort = useCallback((column: string) => {
    setUsersWithoutSort((prev) => ({
      column,
      order: prev.column === column && prev.order === "asc" ? "desc" : "asc",
    }));
    setUsersWithoutPage(1);
  }, []);

  const topPerformersPadded = useMemo(() => {
    const n = 10;
    if (topPerformersData.length >= n) return topPerformersData.slice(0, n);
    return [...topPerformersData, ...Array(n - topPerformersData.length).fill(null)];
  }, [topPerformersData]);

  if (isLoading && hasNoReports) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Stack sx={{ maxWidth: "1444px", width: "100%" }} gap={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <AdminHeader title="Monthly Report" subtitle="Team engagement overview and insights" />
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            {monthlyReportDistributionList && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate("/admin/scheduled-reports/distribution-list")}
                startIcon={<PeopleIcon />}
              >
                Distribution List
              </Button>
            )}
            {!hasNoReports && reportData && (
              <Stack direction="column" spacing={1}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleDownload}
                  disabled={isDownloading}
                  startIcon={<DownloadIcon />}
                >
                  {isDownloading ? "Downloading..." : "Download Report"}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setRegenerateModalOpen(true)}
                  startIcon={<RefreshIcon />}
                >
                  Regenerate Report
                </Button>
              </Stack>
            )}
            {canGenerateLastMonth && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerate}
                disabled={generateMutation.isPending}
              >
                {generateMutation.isPending ? "Generating..." : `Generate ${lastMonth.format("MMMM YYYY")} Report`}
              </Button>
            )}
          </Stack>
        </Stack>

        {generateMutation.isError && (
          <Typography color="error" sx={{ mt: -2 }}>
            {(generateMutation.error as Error)?.message ?? "Generate failed"}
          </Typography>
        )}
        {regenerateMutation.isError && (
          <Typography color="error" sx={{ mt: -2 }}>
            {(regenerateMutation.error as Error)?.message ?? "Regenerate failed"}
          </Typography>
        )}
        {regenerateMutation.isSuccess && (
          <Typography color="success.main" sx={{ mt: -2 }}>
            Report regenerated successfully.
          </Typography>
        )}
        {downloadError && (
          <Typography color="error" sx={{ mt: -2 }}>{downloadError}</Typography>
        )}

        <ScheduledReportsMonthSelector
          selectedMonth={selectedMonth}
          hasNoReports={hasNoReports}
          monthPickerAnchor={monthPickerAnchor}
          pickerYear={pickerYear}
          canGoPrevious={canGoPrevious}
          canGoNext={canGoNext}
          canGoToPreviousYear={canGoToPreviousYear}
          canGoToNextYear={canGoToNextYear}
          hasReport={hasReport}
          onPreviousMonth={goToPreviousMonth}
          onNextMonth={goToNextMonth}
          onMonthPickerOpen={handleMonthPickerOpen}
          onMonthPickerClose={handleMonthPickerClose}
          onMonthSelect={handleMonthSelect}
          onPickerYearChange={handlePickerYearChange}
        />

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : hasNoReports ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 8, textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "white", mb: 2 }}>
              No Reports Available
            </Typography>
            <Typography variant="body1" sx={{ color: "white", mb: 3 }}>
              Generate your first report to see team engagement data. Please note, this mid-month generate will contain slightly different data, and scheduled reports will be generated at the beginning of every subsequent month.
            </Typography>
            {canGenerateLastMonth && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerate}
                disabled={generateMutation.isPending}
                size="large"
              >
                {generateMutation.isPending ? "Generating..." : `Generate ${lastMonth.format("MMMM YYYY")} Report`}
              </Button>
            )}
          </Box>
        ) : reportData && overviewData ? (
          <>
            <ScheduledReportsOverviewCards overviewData={overviewData} />

            <ScheduledReportsTopPerformers topPerformers={topPerformersPadded} />

            <ScheduledReportsInsightsTable
              title="Division Insights"
              count={divisionsData.length}
              columns={DIVISION_COLUMNS}
              rows={paginatedDivisions}
              totalCount={sortedDivisions.length}
              sort={divisionsSort}
              onSort={handleDivisionsSort}
              page={divisionsPage}
              onPageChange={setDivisionsPage}
            />

            <ScheduledReportsInsightsTable
              title="Supervisor Insights"
              count={supervisorsData.length}
              columns={SUPERVISOR_COLUMNS}
              rows={paginatedSupervisors}
              totalCount={sortedSupervisors.length}
              sort={supervisorsSort}
              onSort={handleSupervisorsSort}
              page={supervisorsPage}
              onPageChange={setSupervisorsPage}
            />

            <ScheduledReportsUsersWithoutTable
              count={usersWithoutData.length}
              rows={paginatedUsersWithout}
              totalCount={sortedUsersWithout.length}
              sort={usersWithoutSort}
              onSort={handleUsersWithoutSort}
              page={usersWithoutPage}
              onPageChange={setUsersWithoutPage}
            />
          </>
        ) : null}

        {regenerateModalOpen && (
          <RegenerateReportModal
            open={regenerateModalOpen}
            onClose={() => setRegenerateModalOpen(false)}
            onConfirm={handleRegenerate}
            isPending={regenerateMutation.isPending}
          />
        )}
      </Stack>
    </Box>
  );
};
